// Learner bindings. /learner/progress is a DUMB MIRROR of the iOS
// [setId: QuestionSetProgress] JSON blob (server stores it verbatim, no
// validation, last-writer-wins). The web reads it to drive Train composition and
// (guarded) writes it back. Dates inside the blob are ISO8601 WITHOUT fractional
// seconds — Swift's .iso8601 decoder rejects millis, so writes must match.
import { api } from "./client";
import type {
  AbilityTrend,
  BreakdownWindow,
  MistakeFocus,
  ProgressPeriod,
  ProgressWindow,
  StatsBreakdown,
  StatsSnapshot,
  TreeResponse,
} from "./stats-types";

export interface ProgressMirror {
  state: Record<string, unknown>;
  updated_at: string;
}

// Languages the learner is mid-course on (has done ≥1 language-trainer set).
// Empty array if none — used to drive the "Continue learning" CTA. Auth-gated.
export interface LanguageProgress {
  language_code: string;
  cefr_level: string;
  last_studied_at: string;
}

export function getLanguages(): Promise<LanguageProgress[]> {
  return api.get<LanguageProgress[]>("/learner/languages");
}

export function getProgress(): Promise<ProgressMirror> {
  return api.get<ProgressMirror>("/learner/progress");
}

// ── Per-language placement (ground-up leveling) ─────────────────────────────
// Distinct from the global /calibrate (Community unlock); sets the per-language
// CEFR level + seeds the skill inventory. Read-only status probe + the apply.

export interface LanguagePlacementStatus {
  language_code: string;
  needs_placement: boolean;
  cefr_level: string | null;
}

export interface LanguagePlacementResult {
  language_code: string;
  cefr_level: string;
  seeded_skill_count: number;
  placed_at: string;
}

export function getLanguagePlacementStatus(
  language: string,
): Promise<LanguagePlacementStatus> {
  return api.get<LanguagePlacementStatus>("/learner/language-placement", {
    query: { language },
  });
}

export function applyLanguagePlacement(
  entryTestSessionId: string,
  languageCode: string,
): Promise<LanguagePlacementResult> {
  return api.post<LanguagePlacementResult>("/learner/language-placement", {
    entry_test_session_id: entryTestSessionId,
    language_code: languageCode,
  });
}

export function putProgress(state: Record<string, unknown>): Promise<ProgressMirror> {
  return api.put<ProgressMirror>("/learner/progress", { state });
}

// ── Personal stats (Stream D) ───────────────────────────────────────────────

export interface ProgressionResponse {
  total_xp: number;
  tier: string;
  tier_rung_index: number;
  current_streak: number;
  longest_streak: number;
  last_active_date: string | null;
  calibrated_at: string | null;
}

export function getProgression(): Promise<ProgressionResponse> {
  return api.get<ProgressionResponse>("/learner/progression");
}

export function getLearnerStats(): Promise<StatsSnapshot> {
  return api.get<StatsSnapshot>("/learner/stats");
}

export function getLearnerTree(region?: string): Promise<TreeResponse> {
  return api.get<TreeResponse>("/learner/tree", { query: { region } });
}

export function getLearnerTrend(): Promise<AbilityTrend> {
  return api.get<AbilityTrend>("/learner/stats/trend");
}

export function getMistakes(): Promise<MistakeFocus> {
  return api.get<MistakeFocus>("/learner/mistakes");
}

// Activity window (answered/accuracy/XP/minutes over total|weekly|monthly).
// Backend moved this off the shadowed GET /learner/progress to GET /learner/activity.
export function getActivity(period: ProgressPeriod): Promise<ProgressWindow> {
  return api.get<ProgressWindow>("/learner/activity", { query: { period } });
}

// Strengths & gaps: windowed accuracy ranked by topic / set / framework. One
// payload carries all three groupings; the UI picks the dimension to show.
// Excludes archived + never-practiced sets (unless includeArchived).
export function getStatsBreakdown(
  window: BreakdownWindow,
  includeArchived: boolean,
): Promise<StatsBreakdown> {
  return api.get<StatsBreakdown>("/learner/stats/breakdown", {
    query: { window, include_archived: includeArchived },
  });
}
